/**
 * Active A/B testing experiments.
 */
export enum Experiment {
  summary_page_copy_relaunch_4113 = 'summary_page_copy_relaunch_4113',
  design_v4_4359 = 'design_v4_4359',
  ai_label_13537 = 'ai_label_13537',
  // Used just as a feature flag. No activation of the experiment on the backend.
  google_billing_7517 = 'google_billing_7517',
  // Experiment to be only used in tests
  test_experiment = 'test_experiment',
  backlink_8567 = 'backlink_8567',
  new_copy_pdf_v2_13448 = 'new_copy_pdf_v2_13448',
  ai_guide_flow_13747 = 'ai_guide_flow_13747',
  ai_old_vs_pbp_18613 = 'ai_old_vs_pbp_18613',
  bookmarks_14994 = 'bookmarks_14994',
  exp_ios_external_link_payment_16218 = 'exp_ios_external_link_payment_16218',
  exp_null_experiment_17503 = 'exp_null_experiment_17503',
  exp_first_guide_activation_17817 = 'exp_first_guide_activation_17817',
  exp_annotator_rewrite_18170 = 'exp_annotator_rewrite_18170',
  exp_passwordless_signup_18304 = 'exp_passwordless_signup_18304',
  exp_first_launch_want_to_read_18947 = 'exp_first_launch_want_to_read_18947',
  exp_trial_by_plan_v2_18369 = 'exp_trial_by_plan_v2_18369',
  exp_cancel_save_flow_17977 = 'exp_cancel_save_flow_17977',
  exp_free_botd_18484 = 'exp_free_botd_18484',
  exp_emotional_intelligence_18788 = 'exp_emotional_intelligence_18788',
  exp_career_ascension_18788 = 'exp_career_ascension_18788',
  exp_intellectual_mastery_18788 = 'exp_intellectual_mastery_18788',
  exp_discipline_systems_18788 = 'exp_discipline_systems_18788',
  exp_monthly_annual_upgrade_flow_19092 = 'exp_monthly_annual_upgrade_flow_19092',
  exp_billing_partnership_hesitation_redesign_19142 = 'exp_billing_partnership_hesitation_redesign_19142',
  exp_summary_page_pricing_exit_intent_19129 = 'exp_summary_page_pricing_exit_intent_19129',
  exp_onboarding_checklist_19055 = 'exp_onboarding_checklist_19055',
  exp_billing_momentum_based_checkout_modal_19227 = 'exp_billing_momentum_based_checkout_modal_19227',
  exp_return_to_value_backend_19051 = 'exp_return_to_value_backend_19051',
  exp_homepage_hero_goal_selector_19253 = 'exp_homepage_hero_goal_selector_19253',
  exp_continue_reading_19038 = 'exp_continue_reading_19038',
  exp_billing_hesitation_discount_isolation_19530 = 'exp_billing_hesitation_discount_isolation_19530',
  exp_billing_fud_reduction_redesign_19568 = 'exp_billing_fud_reduction_redesign_19568',
  exp_trial_details_progress_19984 = 'exp_trial_details_progress_19984',
}

/**
 * Hint for the checkResponse method.
 */
export enum CheckResponseHint {
  register = 'register',
  login = 'login',
  passwordReset = 'passwordReset',
  abStartError = 'abStartError',
  updateUserData16218 = 'updateUserData',
}

/**
 * A type definition for the dictionary of experiments and assigned variants:
 * {
 *      experiment_X: 'variant1',
 *      site_page_1208: 'variant2',
 *  }
 */
export type Experiments = { [Key in Experiment]: string | null }

/**
 * A/B experiment and assigned variant data returned by the backend.
 */
export interface ABVariantData {
  experiment: Experiment
  variant: string
  client_id: string
  config?: any
}

export type ExperimentsDict = { [experiment: string]: string }

/**
 * A/B experiments data - client id and all active experiments for the user.
 */
export interface ABExperimentsData {
  client_id: string | null
  experiments: ExperimentsDict
}

/**
 * The backend response can contain a side-loaded
 * A/B testing information:
 * {
 *   "data": { ... regular response ... },
 *   "ab": { ... experiments / assigned variants data ... }
 *  }
 *  This way we can start experiments without sending additional
 *  API request to /ab/start endpoint.
 */
export interface ABSideloadedResponse {
  ab: ABExperimentsData
}

/**
 * EventData is the general A/B testing event information.
 *
 * We include current `url`, the `referrer` we came from,
 * client's `platform` and `os`.
 *
 * The `client_id` is optional, we may or may not have set
 * it yet on the client side.
 *
 * The `experiments` is an optional array of the A/B testing
 * experiments already active on the client.
 */
export type EventData = {
  client_id: string | null
  url: string
  referrer: string
  platform: string
  os: string
  mixpanel_device_id?: string
  experiments?: Experiments
}

/**
 * StartEvent represents the data for the /ab/start call.
 *
 * Besides the general data about the event from `EventData`,
 * we also have `experiment` here - the experiment to start.
 *
 * There is no `event` (it is set to "start" on the server)
 * and no `variant` (it is not assigned yet, server will return it).
 */
export interface StartEvent extends EventData {
  experiment: string
  variant: string | null
}

/**
 * ConvertEvent represents the data for the /ab/event call.
 *
 * We may have many conversion events for the same experiment.
 * The `event` is the event name ('signup', 'card save', etc).
 *
 * The `variant` is the value that was assigned to the client
 * previously and `experiment` is the experiment name we send
 * the conversion `event` for.
 */
export interface ConvertEvent extends EventData {
  event: string
  variant: string
  experiment: string
}

export type TrialByPlanV2_18369Variant = 'A' | 'B' | null
export type IosExternalLinkPayment16218Variant = 'A' | 'B' | null
export type NullExperiment17503Variant = 'A' | 'B' | null
export type FirstGuideActivation17817Variant = 'A' | 'B' | null
export type AnnotatorRewrite18170Variant = 'A' | 'B' | null
export type PasswordlessSignup18304Variant = 'A' | 'B' | null
export type CancelSaveFlow17977Variant = 'A' | 'B' | null
export type FreeBookOfTheDay18484Variant = 'A' | 'B' | null
export type GoalProblemPage18788Variant = 'A' | 'B' | null
export type FirstLaunchWantToRead18947Variant = 'A' | 'B' | null
export type MonthlyAnnualUpgradeFlow19092Variant = 'A' | 'B' | null
export type PartnershipHesitationDiscount19142Variant = 'A' | 'B' | null
export type SummaryPagePricingExitIntent19129Variant = 'A' | 'B' | null
export type OnboardingChecklist19055Variant = 'A' | 'B' | '_EXCLUDED_' | null
export type MomentumBasedCheckout19227Variant = 'A' | 'B' | null
export type ReturnToValueBackend19051Variant = 'A' | 'B' | null
export type HomepageHeroGoalSelector19253Variant = 'A' | 'B' | 'C' | null
export type ContinueReading19038Variant = 'A' | 'B' | null
export type HesitationDiscountIsolation19530Variant = 'A' | 'B' | null
export type BillingFudReduction19568Variant = 'A' | 'B' | null
